import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Import pages
import { HomePage } from "@/pages/HomePage";
import { ShopPage } from "@/pages/ShopPage";
import { CollectionPage } from "@/pages/CollectionPage";
import { ProductPage } from "@/pages/ProductPage";
import { CartPage } from "@/pages/CartPage";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { OrderConfirmationPage } from "@/pages/OrderConfirmationPage";
import { AdminPage } from "@/pages/AdminPage";
import { Layout } from "@/components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Cart Context
const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [sessionId] = useState(() => {
    let id = localStorage.getItem("sessionId");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("sessionId", id);
    }
    return id;
  });

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (e) {
      console.error("Error fetching cart:", e);
    }
  };

  const addToCart = async (item) => {
    try {
      await axios.post(`${API}/cart/${sessionId}/add`, item);
      await fetchCart();
      return true;
    } catch (e) {
      console.error("Error adding to cart:", e);
      return false;
    }
  };

  const removeFromCart = async (artworkId) => {
    try {
      await axios.post(`${API}/cart/${sessionId}/remove?artwork_id=${artworkId}`);
      await fetchCart();
    } catch (e) {
      console.error("Error removing from cart:", e);
    }
  };

  const clearCart = async () => {
    try {
      await axios.post(`${API}/cart/${sessionId}/clear`);
      await fetchCart();
    } catch (e) {
      console.error("Error clearing cart:", e);
    }
  };

  const cartTotal = cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    fetchCart();
  }, []);

  return (
    <CartContext.Provider value={{ cart, cartTotal, cartCount, addToCart, removeFromCart, clearCart, sessionId, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};

// User Context
const UserContext = createContext();

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("user");
    return saved ? JSON.parse(saved) : null;
  });

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      setUser(response.data);
      localStorage.setItem("user", JSON.stringify(response.data));
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || "Login failed" };
    }
  };

  const register = async (email, name, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, name, password });
      setUser(response.data);
      localStorage.setItem("user", JSON.stringify(response.data));
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || "Registration failed" };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
  };

  return (
    <UserContext.Provider value={{ user, login, register, logout }}>
      {children}
    </UserContext.Provider>
  );
};

function App() {
  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (e) {
        // Already seeded or error - ignore
      }
    };
    seedData();
  }, []);

  return (
    <UserProvider>
      <CartProvider>
        <BrowserRouter>
          <Layout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/collection/:collectionId" element={<CollectionPage />} />
              <Route path="/artwork/:artworkId" element={<ProductPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
              <Route path="/admin" element={<AdminPage />} />
            </Routes>
          </Layout>
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </CartProvider>
    </UserProvider>
  );
}

export default App;
